import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CircleCheck, CircleX, TriangleAlert, Info, Lock, Loader2, X } from 'lucide-react';
import toast, { resolveValue } from 'react-hot-toast';

// One look for every pop-up message on the site (FECOA-2174).

const TONES = {
  success: { Icon: CircleCheck, icon: 'text-success-600 dark:text-success-400' },
  error: { Icon: CircleX, icon: 'text-danger-600 dark:text-danger-400' },
  warning: { Icon: TriangleAlert, icon: 'text-warning-500 dark:text-warning-400' },
  info: { Icon: Info, icon: 'text-info-500 dark:text-info-400' },
  lock: { Icon: Lock, icon: 'text-gray-500 dark:text-gray-400' },
  loading: { Icon: Loader2, icon: 'text-primary-600 animate-spin dark:text-primary-400' },
};

// Emoji icons some callers pass to toast() map onto the matching tone.
const EMOJI_TONES = {
  '⚠️': 'warning',
  '⚠': 'warning',
  'ℹ️': 'info',
  'ℹ': 'info',
  '🔒': 'lock',
};

export const toneFor = (t) => {
  if (t.type !== 'blank' && t.type !== 'custom' && TONES[t.type]) return t.type;
  if (t.tone && TONES[t.tone]) return t.tone;
  if (typeof t.icon === 'string' && EMOJI_TONES[t.icon]) return EMOJI_TONES[t.icon];
  return 'info';
};

// Error handlers sometimes pass a whole error payload; show its text instead of crashing.
export const messageText = (message) => {
  if (message == null || typeof message !== 'object' || React.isValidElement(message) || Array.isArray(message)) {
    return message;
  }
  return message.message || message.detail || message.error || JSON.stringify(message);
};

const CustomToast = ({ t }) => {
  const tone = toneFor(t);
  const { Icon, icon } = TONES[tone];
  const customIcon = t.icon && typeof t.icon !== 'string' ? t.icon : null;
  const message = messageText(resolveValue(t.message, t));

  return (
    <AnimatePresence>
      {t.visible && (
        <motion.div
          layout
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0, transition: { duration: 0.18, ease: 'easeOut' } }}
          exit={{ opacity: 0, transition: { duration: 0.12, ease: 'easeIn' } }}
          className="pointer-events-auto flex w-[min(380px,calc(100vw-2rem))] items-start gap-3 rounded-xl border border-gray-200/80 bg-white py-3 pl-3.5 pr-2 shadow-float dark:border-white/10 dark:bg-gray-800"
          data-toast-tone={tone}
        >
          <span className="mt-px flex h-5 w-5 flex-shrink-0 items-center justify-center" aria-hidden="true">
            {customIcon || <Icon className={`h-[18px] w-[18px] ${icon}`} strokeWidth={2} />}
          </span>
          <div className="min-w-0 flex-1 break-words pt-px text-[13.5px] leading-5 text-gray-800 dark:text-gray-100" {...t.ariaProps}>
            {message}
          </div>
          {t.type !== 'loading' && (
            <button
              type="button"
              onClick={() => toast.dismiss(t.id)}
              className="-my-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 dark:hover:bg-gray-700 dark:hover:text-gray-200"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" strokeWidth={2} />
            </button>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CustomToast;
